/**
 * Localization Utilities
 *
 * Shared utilities for handling Strapi i18n localized content.
 * Uses Strapi's native `localizations` relationship to merge translations.
 */

// =============================================================================
// Types
// =============================================================================

/**
 * Localized text with English (required) and Arabic (optional)
 */
export interface LocalizedText {
  en: string;
  ar?: string;
}

/**
 * Strapi localization entry (from ?populate=localizations response)
 */
export interface StrapiLocalization {
  id?: number;
  documentId?: string;
  locale: string;
  name?: string | null;
  description?: string | null;
  [key: string]: unknown;
}

/**
 * Strapi DTO with localization fields
 */
export interface StrapiLocalizedDto {
  locale?: string;
  localizations?: StrapiLocalization[] | null;
  [key: string]: unknown;
}

// =============================================================================
// Merge Localizations (API Response → Domain)
// =============================================================================

/**
 * Merge localizations from Strapi response into a LocalizedText object.
 *
 * Strapi returns the current locale's value directly, and other locales
 * in the `localizations` array. This function combines them.
 *
 * IMPORTANT: When requesting locale=en but the entity only exists in Arabic,
 * Strapi returns the Arabic entry with locale="ar" and empty localizations.
 * This function detects this case and assigns values correctly.
 *
 * @example
 * // Strapi response with ?populate=localizations
 * const dto = {
 *   name: "Main Gate",
 *   locale: "en",
 *   localizations: [{ locale: "ar", name: "البوابة الرئيسية" }]
 * }
 * const name = mergeLocalizations(dto, 'name')
 * // Result: { en: "Main Gate", ar: "البوابة الرئيسية" }
 *
 * @example
 * // Edge case: entity only exists in Arabic (no English locale created)
 * const dto = {
 *   name: "مصنع رابغ",
 *   locale: "ar",
 *   localizations: []
 * }
 * const name = mergeLocalizations(dto, 'name')
 * // Result: { en: "مصنع رابغ", ar: "مصنع رابغ" } - uses Arabic as fallback for both
 */
export function mergeLocalizations<T extends StrapiLocalizedDto>(
  dto: T,
  field: string
): LocalizedText {
  const result: LocalizedText = { en: '' };

  // Get value from current locale
  const currentValue = dto[field] as string | undefined;
  const currentLocale = dto.locale || 'en';

  if (currentValue) {
    if (currentLocale === 'ar') {
      result.ar = currentValue;
    } else {
      result.en = currentValue;
    }
  }

  // Merge values from localizations array
  if (dto.localizations && Array.isArray(dto.localizations)) {
    for (const loc of dto.localizations) {
      const locValue = loc[field] as string | undefined;
      if (locValue) {
        if (loc.locale === 'ar') {
          result.ar = locValue;
        } else if (loc.locale === 'en') {
          result.en = locValue;
        }
      }
    }
  }

  // Fallback: if English is missing, use Arabic; if Arabic is missing, use English
  if (!result.en && result.ar) {
    result.en = result.ar;
  }
  if (!result.ar) {
    result.ar = result.en;
  }

  return result;
}

/**
 * Merge Strapi localizations for **edit forms**: does **not** copy English into Arabic when Arabic is missing.
 * Use for form default values so Arabic inputs stay empty until translated.
 * For list/display strings, use {@link mergeLocalizations} or {@link getLocalizedValue}.
 */
export function mergeLocalizationsStrict<T extends StrapiLocalizedDto>(
  dto: T,
  field: string
): LocalizedText {
  const result: LocalizedText = { en: '' };

  const currentValue = dto[field] as string | undefined | null;
  const currentLocale = (dto.locale || 'en').split('-')[0];

  if (currentValue != null && String(currentValue).trim() !== '') {
    const s = String(currentValue);
    if (currentLocale === 'ar') {
      result.ar = s;
    } else {
      result.en = s;
    }
  }

  if (dto.localizations && Array.isArray(dto.localizations)) {
    for (const loc of dto.localizations) {
      const locLocale = (loc.locale || '').split('-')[0];
      const locValue = loc[field] as string | undefined | null;
      if (locValue == null || String(locValue).trim() === '') continue;
      const v = String(locValue);
      if (locLocale === 'ar') {
        result.ar = v;
      } else if (locLocale === 'en') {
        result.en = v;
      }
    }
  }

  if (!result.en.trim() && result.ar) {
    result.en = result.ar;
  }

  return result;
}

/**
 * Create LocalizedText from a single string (fallback when no localizations available)
 *
 * @example
 * const name = createLocalizedText("Main Gate", "en")
 * // Result: { en: "Main Gate", ar: "Main Gate" }
 */
export function createLocalizedText(
  value: string,
  locale: string = 'en'
): LocalizedText {
  const result: LocalizedText = { en: value };

  if (locale === 'ar') {
    result.ar = value;
    result.en = value; // fallback
  } else {
    result.ar = value; // fallback
  }

  return result;
}

// =============================================================================
// Get Localized Value (Domain → Display)
// =============================================================================

/**
 * Get the localized value from a LocalizedText object based on current language.
 * Falls back to English if the requested language is not available.
 *
 * @example
 * const name = { en: "Main Gate", ar: "البوابة الرئيسية" }
 * getLocalizedValue(name, 'ar') // "البوابة الرئيسية"
 * getLocalizedValue(name, 'en') // "Main Gate"
 * getLocalizedValue(undefined, 'en') // ""
 */
export function getLocalizedValue(
  value: LocalizedText | string | undefined | null,
  language: string = 'en'
): string {
  if (!value) return '';

  // If it's already a string, return it
  if (typeof value === 'string') return value;

  // Get value for requested language, fallback to English
  if (language === 'ar') {
    return value.ar || value.en || '';
  }

  return value.en || value.ar || '';
}

/**
 * Check if a LocalizedText has both English and Arabic values
 */
export function hasAllTranslations(
  value: LocalizedText | undefined | null
): boolean {
  if (!value) return false;
  return Boolean(value.en && value.ar && value.en !== value.ar);
}

/**
 * Check if Arabic translation is missing (or same as English)
 */
export function isMissingArabic(
  value: LocalizedText | undefined | null
): boolean {
  if (!value) return true;
  return !value.ar || value.ar === value.en;
}

// =============================================================================
// Query String Builders
// =============================================================================

/**
 * Build query string to populate localizations in Strapi request.
 * Add this to your API calls to get both locale versions.
 *
 * @example
 * const params = new URLSearchParams()
 * addLocalizationsPopulate(params)
 * // Adds: populate[localizations][fields][0]=name
 * //       populate[localizations][fields][1]=description
 * //       populate[localizations][fields][2]=locale
 */
export function addLocalizationsPopulate(
  params: URLSearchParams,
  fields: string[] = ['name', 'description', 'locale']
): void {
  fields.forEach((field, index) => {
    params.set(`populate[localizations][fields][${index}]`, field);
  });
}

/**
 * Build the default populate string for localizations
 */
export function getLocalizationsPopulateParams(
  fields: string[] = ['name', 'description', 'locale']
): Record<string, string> {
  const params: Record<string, string> = {};
  fields.forEach((field, index) => {
    params[`populate[localizations][fields][${index}]`] = field;
  });
  return params;
}
